import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useStagePageContext, useVenuePageContext } from "Venues/contexts";
import { dataMetaSelector, dispatchGetStageEquipmentAction } from "Venues/state";
import { IEquipmentSubcategoryItem } from "Venues/types";

import { EquipmentTable, EquipmentTableType } from "Venues/components";
import { getStageEquipmentKey, useSelectStageEquipment } from "Venues/utils";

interface EquipmentOverviewProps {
  category: IEquipmentSubcategoryItem;
}

export const EquipmentOverview: React.FC<EquipmentOverviewProps> = ({ category }) => {
  const dispatch = useDispatch();
  const { venueId } = useVenuePageContext();
  const { stageId } = useStagePageContext();

  const { pagination, equipment } = useSelectStageEquipment(stageId || "", category.category);

  const { api: apiMeta } = useSelector(
    dataMetaSelector(getStageEquipmentKey(stageId, category.category || ""))
  );

  const fetchEquipment = useCallback(
    (page?: number) => {
      if (!stageId) {
        return;
      }

      dispatchGetStageEquipmentAction(dispatch, {
        venue: venueId,
        stage: stageId,
        category: category.category,
        page,
      });
    },
    [dispatch, stageId, venueId, category.category]
  );

  // fetch equipment for category once
  const fetchedEquipmentOnce = useRef(false);
  useEffect(() => {
    if (!stageId || fetchedEquipmentOnce.current) {
      return;
    }

    fetchedEquipmentOnce.current = true;

    fetchEquipment();
  }, [stageId, fetchEquipment]);

  return (
    <EquipmentTable
      equipment={equipment}
      type={EquipmentTableType.MoreInfo}
      apiStatus={apiMeta?.status}
      pager={
        pagination && {
          ...pagination,
          onPageChange: fetchEquipment,
        }
      }
      fetchEquipment={fetchEquipment}
    />
  );
};
